import React, { useState, useEffect } from "react";
import UploadFile from "../components/UploadFile";
import DarkModeToggle from "../components/DarkModeToggle";
import { useActiveModel } from "../context/ActiveModelContext";
import { useAuth } from "../context/AuthContext";
import "../components/user-menu.css";
import { getProjects } from "../api";

export default function DashboardLayout({
  children,
  datasets = [],
  selectedDataset,
  onSelectDataset,
  onSelectDatasetId,
  datasetDetails = [],
  modelTypes = [],
  selectedModel,
  onSelectModel,
  onDataReceived,
  onFeedbackClick,
  onUserDashboardClick,
  onAdminLoginClick,
  onDeleteDataset, 
  onRenameDataset,
  selectedDatasetId, 
}) {
  const { activeModel, clearActiveModel } = useActiveModel();
  const { user } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
const [fetchedProjects, setFetchedProjects] = useState([]);
const [localDatasetDetails, setLocalDatasetDetails] = useState(datasetDetails);

// load projects and datasets like UploadFile.js
useEffect(() => {
  const loadProjectsAndDatasets = async () => {
    try {
      //Get the list of projects from the backend
      const projResp = await getProjects(); 
      const projectsList = projResp?.projects || [];
      setFetchedProjects(projectsList);

      //Fetch datasets for each project
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:8000";
      const token = localStorage.getItem("auth_token");
      const headers = {
        "Content-Type": "application/json",
        ...(token && { "Authorization": `Bearer ${token}` }),
      };

      const allDetails = [];
      for (const projectName of projectsList) {
        // fetch datasets for this project
        const url = `${API_BASE_URL}/projects?project_name=${encodeURIComponent(projectName)}`;
        const resp = await fetch(url, { headers });
        if (!resp.ok) continue;
        const body = await resp.json();
        // backend returns either an array of dataset records (for project_name) or the project name list (when project_name omitted).
        const rows = Array.isArray(body) ? body : body?.projects || body?.results || body;
        if (!rows) continue;

        for (const r of rows) {
          // normalize DB row / demo row shape into expected fields
          allDetails.push({
            id: r.id ?? null,
            name: r.name ?? r[ "name" ] ?? (r.filename ? r.filename.replace(/\.[^.]+$/, "") : null),
            filename: r.filename ?? r.file_name ?? null,
            project_name: projectName,
            file_path: r.file_path ?? null,
            user_id: r.user_id ?? null,
            file_size: r.file_size ?? r.file_size_bytes ?? null,
            row_count: r.row_count ?? null,
            column_count: r.column_count ?? null,
            column_names: r.column_names ?? null,
            is_original: r.is_original ?? false,
            created_at: r.created_at ?? null,
            updated_at: r.updated_at ?? null,
          });
        }
      }

      setLocalDatasetDetails(allDetails);
    } catch (err) {
      console.error("Failed loading projects/datasets:", err);
    }
  };

  loadProjectsAndDatasets();
}, []);

  //Groups datasets together by project
  const groupDatasetsByProject = (datasetList = []) => {
  return datasetList.reduce((acc, dataset) => {
    // Fallback if project_name isn't provided
    const projectKey = dataset.project_name || 'Unassigned Projects';
    
    if (!acc[projectKey]) {
      acc[projectKey] = [];
    }
    
    acc[projectKey].push(dataset);
    return acc;
  }, {});
};

// Track which project accordions/tabs are expanded: { "Sales Project": true }
  const [expandedProjects, setExpandedProjects] = useState({});

  // Group flat DB records into a project map
  const projectsMap = groupDatasetsByProject(localDatasetDetails.length ? localDatasetDetails : datasetDetails);

  // Toggle open/close state of a project tab
  const toggleProject = (projectName) => {
    setExpandedProjects((prev) => ({
      ...prev,
      [projectName]: !prev[projectName]
    }));
  };

  // Clear active model when user state changes (login/logout)
  useEffect(() => {
    clearActiveModel();
  }, [user, clearActiveModel]);

  return (
    <div className="layout">
      {/* Dark Mode Toggle */}
      <DarkModeToggle />
      
      {/* Sidebar */}
      <aside className="sidebar">
        <div className="sidebar-header">
          <h2>Datasets</h2>
        </div>
        <div className="sidebar-upload">
          <UploadFile onDataReceived={onDataReceived} />
        </div>
        <nav className="sidebar-nav">
        {Object.keys(projectsMap).length > 0 ? (
          Object.entries(projectsMap).map(([projectName, datasetsInProject]) => {
            const isExpanded = expandedProjects[projectName];

            return (
              <div key={projectName} className="project-group" style={{ marginBottom: '12px' }}>
                {/* 1. PROJECT TAB / HEADER */}
                <button
                  className="project-tab-header"
                  onClick={() => toggleProject(projectName)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 10px',
                    backgroundColor: '#1e293b',
                    color: '#f8fafc',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                    fontSize: '0.8rem'
                  }}
                >
                  <span>📁 {projectName}</span>
                  <span style={{ fontSize: '0.65rem', opacity: 0.7 }}>
                    {isExpanded ? '▼' : '▶'} ({datasetsInProject.length})
                  </span>
                </button>

                {/* 2. NESTED DATASETS FOR THIS PROJECT */}
                {isExpanded && (
                  <div className="project-dataset-list" style={{ paddingLeft: '10px', marginTop: '4px' }}>
                    {datasetsInProject.map((ds) => {
                      const isActive = selectedDatasetId === ds.id;

                      return (
                        <div 
                          key={ds.id} 
                          style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            marginBottom: '4px',
                            backgroundColor: isActive ? '#334155' : 'transparent',
                            borderRadius: '4px',
                            paddingRight: '4px'
                          }}
                        >
                          {/* File Selection Button */}
                          <button
                            className={`sidebar-item ${isActive ? "active" : ""}`}
                            style={{ 
                              flexGrow: 1, 
                              margin: 0, 
                              overflow: 'hidden', 
                              textOverflow: 'ellipsis',
                              textAlign: 'left',
                              background: 'none',
                              border: 'none',
                              color: '#e2e8f0',
                              padding: '6px 8px',
                              cursor: 'pointer',
                              fontSize: '0.75rem'
                            }}
                            onClick={() => {
                              if (onSelectDatasetId) onSelectDatasetId(ds.id);
                              if (onSelectDataset) onSelectDataset(ds.name || ds.filename);
                            }}
                          >
                            {/* Visual Tag for Lineage */}
                            <span 
                              style={{ 
                                fontSize: '0.6rem', 
                                padding: '1px 4px', 
                                borderRadius: '3px',
                                marginRight: '6px',
                                backgroundColor: ds.is_original ? '#15803d' : '#b45309',
                                color: 'white'
                              }}
                            >
                              {ds.is_original ? 'RAW' : 'PROC'}
                            </span>
                            {ds.filename || ds.name}
                          </button>

                          {/* Rename Action */}
                          <button 
                            onClick={() => {
                              const newName = window.prompt(`Rename dataset "${ds.name}" to:`, ds.name);
                              if (newName && newName.trim() !== "" && newName !== ds.name && onRenameDataset) {
                                onRenameDataset(ds.id, newName.trim());
                              }
                            }}
                            title="Rename dataset"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '10px' }}
                          >
                            ✏️
                          </button>

                          {/* Delete Action */}
                          <button 
                            onClick={() => {
                              if (window.confirm(`Are you sure you want to delete "${ds.name}"?`) && onDeleteDataset) {
                                onDeleteDataset(ds.name); 
                              }
                            }}
                            title="Delete dataset"
                            style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: '0 4px', fontSize: '10px' }}
                          >
                            ❌
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })
        ) : (
          <p style={{ fontSize: '0.75rem', color: '#94a3b8', padding: '10px' }}>No projects found.</p>
        )}
      </nav>

        <div className="sidebar-header" style={{ marginTop: 16 }}>
          <h2>Models</h2>
        </div>
        <nav className="sidebar-nav">
          {modelTypes && modelTypes.length > 0 ? (
            modelTypes.map((model) => (
              <button
                key={model}
                className={`sidebar-item ${selectedModel === model ? "active" : ""}`}
                onClick={() => onSelectModel && onSelectModel(model)}
              >
                {model}
              </button>
            ))
          ) : (
            <div className="sidebar-item" style={{ cursor: "default", color: "var(--text-muted)" }}>
              No models available
            </div>
          )}
        </nav>

        {/* Active Model */}
        <div className="sidebar-header" style={{ marginTop: 16 }}>
          <h2>Active Model</h2>
        </div>
        <div className="sidebar-card">
          {activeModel ? (
            <>
              <div className="sidebar-item" style={{ cursor: "default" }}>
                <div><strong>ID:</strong> {activeModel.id}</div>
                <div><strong>Dataset:</strong> {activeModel.dataset_name}</div>
                <div><strong>Type:</strong> {activeModel.model_type}</div>
              </div>
              <button className="sidebar-item" onClick={clearActiveModel}>Clear Active Model</button>
            </>
          ) : (
            <div className="sidebar-item" style={{ cursor: "default", color: "var(--text-muted)" }}>
              None selected
            </div>
          )}
        </div>

        {/* Feedback Button at bottom of sidebar */}
        <div className="sidebar-feedback">
          <button
            onClick={onFeedbackClick}
            className="feedback-button"
            title="Open Feedback & Support"
          >
            <svg 
              width="16" 
              height="16" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth="2" 
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
              />
            </svg>
            Feedback & Support
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="main">
        {/* Header */}
        <header className="header">
          <h1>BioDawn Drug Discovery Dashboard</h1>
          
          {/* Admin Login Button */}
          {!user && (
            <div className="admin-login-section">
              <button
                onClick={onAdminLoginClick}
                className="admin-login-btn"
                title="Admin Login"
              >
                🛠️ Admin
              </button>
            </div>
          )}
          
          {/* User Menu */}
          {user && (
            <div className="user-menu">
              <button
                className="user-menu-button"
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                <div className="user-avatar">
                  {user.full_name ? user.full_name.charAt(0).toUpperCase() : user.email.charAt(0).toUpperCase()}
                </div>
                <span className="user-name">{user.full_name || user.email}</span>
                <svg 
                  width="16" 
                  height="16" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`}
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d="M19 9l-7 7-7-7" 
                  />
                </svg>
              </button>
              
              {showUserMenu && (
                <div className="user-menu-dropdown">
                  <div className="user-info">
                    <div className="user-email">{user.email}</div>
                    <div className={`subscription-tier tier-${user.subscription_tier}`}>
                      {user.subscription_tier} Plan
                    </div>
                  </div>
                  <div className="menu-divider"></div>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      onUserDashboardClick && onUserDashboardClick();
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    Account Settings
                  </button>
                  <button 
                    className="menu-item"
                    onClick={() => {
                      setShowUserMenu(false);
                      onFeedbackClick && onFeedbackClick();
                    }}
                  >
                    <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                    Support
                  </button>
                </div>
              )}
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="content">{children}</main>

        {/* Footer */}
        <footer className="footer">© {new Date().getFullYear()} BioDawn Innovations</footer>
      </div>
    </div>
  );
}
